import { buildIntentBasedActionId } from './Action';
import type { v2, SAPUI5_FRAGMENT_CLASS } from '@sap/ux-specification-types';
import { ControlType } from '@sap/ux-specification-types';
import { syncRule, buildColumnControlId } from '../../../common/decoration';
import { addPatternForBindingChangeOfEnumLR, getActionBreadcrumbParts } from '../../../common';
import type { TextAlign, URI } from 'sap/ui/core/library';
import type { ButtonType } from 'sap/m/library';

/**
 * Create stable ID for Action Column.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} idBreadcrumbs - Array of breadcrumb IDs used for generating the stable ID.
 * @param {string[]} breadcrumbs - Array of breadcrumb strings used for action identification.
 * @returns {string} stable ID of Action Column
 */
const buildColumnActionId = (baseId: string, idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const columnTemplate = 'template:::TableColumn';
    const { actionType, actionId } = getActionBreadcrumbParts(breadcrumbs);
    const smartTableId = `sSmartTableId::${idBreadcrumbs[0]}`;
    return `${baseId}${columnTemplate}:::${actionType}:::${smartTableId}:::sAction::${actionId}`;
};
/**
 * Create stable ID for Intent Based Action Column.
 *
 * @param {string} baseId - Base Id of the control.
 * @param {string[]} idBreadcrumbs - Array of breadcrumb IDs used for generating the stable ID.
 * @param {string[]} breadcrumbs - Array of breadcrumb strings used for action identification.
 * @returns {string} stable ID of Action Column
 */
const buildColumnIntentBasedId = (baseId: string, idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const columnTemplate = 'template:::TableColumn';
    const { actionType, actionId, semanticObjectId } = getActionBreadcrumbParts(breadcrumbs);
    const smartTableId = `sSmartTableId::${idBreadcrumbs[0]}`;
    return `${baseId}${columnTemplate}:::${actionType}:::${smartTableId}:::sSemanticObject::${semanticObjectId}:::sAction::${actionId}`;
};

/**
 * Create stable ID for Column Action button.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} _idBreadcrumbs - Array of breadcrumb IDs used for internal purposes.
 * @param {string[]} breadcrumbs - Array of breadcrumb strings used for action identification.
 * @returns {string} stable ID of Column Action button
 */
const buildColumnActionButtonId = (baseId: string, _idBreadcrumbs: string[], breadcrumbs: string[]): string => {
    const { actionId } = getActionBreadcrumbParts(breadcrumbs);
    return `${baseId}action::${actionId.replace(':2f', '::')}`;
};

// TableColumnAction column properties
const flexRuleForColumn = {
    controlId: buildColumnActionId,
    controlType: (): ControlType => ControlType.TableColumn
};

// TableColumnAction action properties
const flexRuleForColumnAction = {
    controlId: buildColumnActionButtonId,
    controlType: (): ControlType => ControlType.Button
};

// TableColumnActionIntentBased main properties
const flexRuleForColumnIntentBased = {
    ...flexRuleForColumn,
    controlId: buildColumnIntentBasedId
};

// TableColumnActionIntentBased action properties
const flexRuleForColumnIntentBasedAction = {
    ...flexRuleForColumnAction,
    controlId: buildIntentBasedActionId
};

export class TableColumn implements v2.TableColumn {
    @syncRule({
        flex: {
            controlId: buildColumnControlId,
            controlType: (): ControlType => ControlType.TableColumn
        }
    })
    width?: string;
    @syncRule({
        flex: {
            controlId: buildColumnControlId,
            controlType: (): ControlType => ControlType.TableColumn
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    hAlign?: TextAlign;
}

export class TableColumnAction implements v2.TableColumnAction {
    @syncRule({
        flex: flexRuleForColumn
    })
    width?: string;

    @syncRule({
        flex: flexRuleForColumn,
        generate: addPatternForBindingChangeOfEnumLR
    })
    hAlign?: TextAlign;

    @syncRule({
        flex: flexRuleForColumnAction
    })
    tooltip?: string;

    @syncRule({
        flex: flexRuleForColumnAction
    })
    icon?: URI;

    @syncRule({
        flex: flexRuleForColumnAction
    })
    activeIcon?: URI;

    @syncRule({
        flex: flexRuleForColumnAction
    })
    type?: ButtonType;
}

export class TableColumnActionIntentBased implements v2.TableColumnAction {
    @syncRule({
        flex: flexRuleForColumnIntentBased
    })
    width?: string;

    @syncRule({
        flex: flexRuleForColumnIntentBased,
        generate: addPatternForBindingChangeOfEnumLR
    })
    hAlign?: TextAlign;

    @syncRule({
        flex: flexRuleForColumnIntentBasedAction
    })
    tooltip?: string;

    @syncRule({
        flex: flexRuleForColumnIntentBasedAction
    })
    icon?: URI;

    @syncRule({
        flex: flexRuleForColumnIntentBasedAction
    })
    activeIcon?: URI;

    @syncRule({
        flex: flexRuleForColumnIntentBasedAction
    })
    type?: ButtonType;
}

/**
 * No sync rules are defined for table custom columns, as they are transferred altogether.
 * This approach is needed to support inserts as well as deletions of manifest extensions and fragments
 */
export class TableCustomColumn implements v2.TableCustomColumn {
    id?: string;
    text?: string;
    columnKey: string;
    columnIndex?: number;
    leadingProperty?: string;
    className: typeof SAPUI5_FRAGMENT_CLASS;
    fragmentName: string;
    cellsFragmentName?: string;
    tabKey?: string;
    extensionType: v2.TableColumnExtensionTypeV2;
}
